/**
 * LAIE — Lead Acquisition & Intelligence Engine (Super-admin only).
 *
 * One-screen console with three tabs:
 *   • Overview  — KPIs, grade split, city heatmap, industry breakdown, follow-ups
 *   • Discovery — target registry, seed URL, bulk seed, "Run discovery now"
 *   • Companies — filterable table, CSV export, per-row promote / delete / view
 *
 * All routes talk to /api/laie/* which is guarded to super_admin server-side.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Rocket,
  Building2,
  Sparkles,
  Radar,
  Globe,
  Trash2,
  ArrowUpRight,
  Download,
  RefreshCw,
  Coffee,
  Flame,
  TrendingUp,
  MapPin,
  ExternalLink,
  Layers,
  ListPlus,
  Play,
  X,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  CalendarClock,
  ShieldCheck,
  Upload,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const GRADE_COLOR = {
  A: "text-primary border-primary/40 bg-primary/10",
  B: "text-chart-3 border-chart-3/40 bg-chart-3/10",
  C: "text-muted-foreground border-border bg-secondary/40",
};

// Compact "05 Aug" for the table; full ISO stays on hover for precision.
const _MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function formatShortDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${String(d.getDate()).padStart(2, "0")} ${_MONTHS[d.getMonth()]}`;
}

export default function LaieOverview() {
  const [tab, setTab] = useState("overview");
  const [meta, setMeta] = useState(null);
  const [stats, setStats] = useState(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const refresh = () => setRefreshCounter((n) => n + 1);

  useEffect(() => {
    api.get("/laie/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/laie/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [refreshCounter]);

  return (
    <div className="space-y-6" data-testid="laie-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-primary mb-2 flex items-center gap-2">
            <Radar className="h-3 w-3" /> Lead Acquisition & Intelligence Engine
          </div>
          <h1 className="font-display text-4xl leading-tight">
            LAIE — <em className="text-primary not-italic">Kairosa's own lead brain.</em>
          </h1>
          <p className="mt-2 text-muted-foreground max-w-2xl text-sm">
            Autonomous B2B discovery across India — scored, deduped, and pushed into Growth Engine
            with one click. Free sources only in Phase 1 (public websites + DuckDuckGo). Paid data
            providers plug in later.
          </p>
        </div>
        <Button
          variant="outline"
          className="rounded-full gap-2"
          onClick={refresh}
          data-testid="laie-refresh"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </Button>
      </div>

      {/* Tab strip */}
      <div className="border-b border-border">
        <div className="flex gap-1">
          {[
            { id: "overview", label: "Overview", Icon: TrendingUp },
            { id: "discovery", label: "Discovery", Icon: Radar },
            { id: "companies", label: "Companies", Icon: Building2 },
          ].map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`laie-tab-${id}`}
              className={
                "px-4 py-2.5 text-sm inline-flex items-center gap-2 border-b-2 -mb-px transition-colors " +
                (tab === id
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground")
              }
            >
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>
      </div>

      {tab === "overview" && <OverviewTab stats={stats} />}
      {tab === "discovery" && <DiscoveryTab meta={meta} onChange={refresh} />}
      {tab === "companies" && <CompaniesTab meta={meta} refreshCounter={refreshCounter} onChange={refresh} />}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  OVERVIEW                                                                  */
/* -------------------------------------------------------------------------- */

function OverviewTab({ stats }) {
  if (!stats) return <div className="text-sm text-muted-foreground py-16 text-center">Loading intelligence…</div>;
  const gradeA = stats.by_grade?.A || 0;
  const gradeB = stats.by_grade?.B || 0;
  const gradeC = stats.by_grade?.C || 0;
  const gradeTotal = gradeA + gradeB + gradeC || 1;

  return (
    <div className="space-y-6">
      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi testid="laie-kpi-total" icon={<Building2 className="h-4 w-4" />} label="Companies" value={stats.total} />
        <Kpi testid="laie-kpi-new-today" icon={<Sparkles className="h-4 w-4" />} label="New today" value={stats.new_today} />
        <Kpi testid="laie-kpi-verified" icon={<Globe className="h-4 w-4" />} label="Verified contact" value={stats.verified} hint="email + phone/LinkedIn" />
        <Kpi testid="laie-kpi-grade-a" icon={<Flame className="h-4 w-4" />} label="Grade A (hot)" value={stats.high_opportunity} tone="primary" />
        <Kpi testid="laie-kpi-promoted" icon={<Rocket className="h-4 w-4" />} label="→ Growth Engine" value={stats.promoted_to_growth} hint="promoted" />
      </div>

      {/* Grade split bar */}
      <div className="rounded-2xl border border-border bg-card p-5" data-testid="laie-grade-split">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-2">
            <Layers className="h-3 w-3" /> Grade distribution
          </div>
          <div className="text-xs text-muted-foreground">
            A={gradeA} · B={gradeB} · C={gradeC}
          </div>
        </div>
        <div className="h-3 rounded-full overflow-hidden flex bg-secondary/40">
          <div className="bg-primary" style={{ width: `${(gradeA / gradeTotal) * 100}%` }} title={`A · ${gradeA}`} />
          <div className="bg-chart-3" style={{ width: `${(gradeB / gradeTotal) * 100}%` }} title={`B · ${gradeB}`} />
          <div className="bg-muted-foreground/40" style={{ width: `${(gradeC / gradeTotal) * 100}%` }} title={`C · ${gradeC}`} />
        </div>
      </div>

      {/* City heatmap + industry breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="laie-by-city">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-3 flex items-center gap-2">
            <MapPin className="h-3 w-3" /> By city (top 12)
          </div>
          {stats.by_city.length === 0 ? (
            <EmptyBlock text="No cities yet — run discovery to see the map fill up." />
          ) : (
            <div className="space-y-2">
              {stats.by_city.map((c) => (
                <div key={c.city} className="flex items-center gap-3">
                  <div className="text-sm w-32 truncate">{c.city}</div>
                  <div className="flex-1 h-2 bg-secondary/50 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary"
                      style={{ width: `${Math.min(100, c.count * 8)}%` }}
                    />
                  </div>
                  <div className="text-xs text-muted-foreground w-14 text-right">{c.count} · Ø{c.avg_score}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-border bg-card p-5" data-testid="laie-by-industry">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-3 flex items-center gap-2">
            <Coffee className="h-3 w-3" /> By industry (top 12)
          </div>
          {stats.by_industry.length === 0 ? (
            <EmptyBlock text="No industries yet — seed a URL or run discovery." />
          ) : (
            <div className="space-y-2">
              {stats.by_industry.map((c) => (
                <div key={c.industry} className="flex items-center gap-3">
                  <div className="text-sm w-40 truncate">{c.industry}</div>
                  <div className="flex-1 h-2 bg-secondary/50 rounded-full overflow-hidden">
                    <div className="h-full bg-chart-3" style={{ width: `${Math.min(100, c.count * 8)}%` }} />
                  </div>
                  <div className="text-xs text-muted-foreground w-14 text-right">{c.count} · Ø{c.avg_score}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Coffee demand heatmap */}
      <div className="rounded-2xl border border-border bg-card p-5" data-testid="laie-coffee-heatmap">
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-3 flex items-center gap-2">
          <Coffee className="h-3 w-3 text-primary" /> Coffee demand heatmap
        </div>
        {stats.coffee_demand_heatmap.length === 0 ? (
          <EmptyBlock text="Coffee-focused leads will appear here after discovery." />
        ) : (
          <div className="flex flex-wrap gap-2">
            {stats.coffee_demand_heatmap.map((h) => (
              <div
                key={h.city}
                className="rounded-lg border border-primary/25 bg-primary/5 px-3 py-2"
                style={{ opacity: Math.max(0.5, Math.min(1, h.coffee_leads / 8)) }}
              >
                <div className="text-sm font-semibold">{h.city}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  {h.coffee_leads} leads · Ø {h.avg_score}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upcoming follow-ups */}
      {stats.upcoming_followups.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="laie-upcoming-followups">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-3">
            Upcoming follow-ups · next 14 days
          </div>
          <div className="divide-y divide-border">
            {stats.upcoming_followups.map((u) => (
              <div key={u.id} className="py-2 flex items-center gap-3 text-sm">
                <span className={"text-[10px] uppercase tracking-[0.2em] border rounded-full px-2 py-0.5 " + (GRADE_COLOR[u.grade] || "")}>
                  {u.grade || "—"}
                </span>
                <div className="flex-1 truncate">{u.company_name}</div>
                <div className="text-xs text-muted-foreground">{u.next_follow_up_date?.slice(0, 10)}</div>
                <div className="text-xs text-muted-foreground">{u.status}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  DISCOVERY                                                                 */
/* -------------------------------------------------------------------------- */

function DiscoveryTab({ meta, onChange }) {
  const [targets, setTargets] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newCity, setNewCity] = useState("");
  const [newIndustries, setNewIndustries] = useState([]);
  // Currently-running job (either a discovery run OR a bulk seed). Only one
  // may run at a time. `runningJob` holds the polled doc.
  const [runningJob, setRunningJob] = useState(null);
  const [seedUrl, setSeedUrl] = useState("");
  const [seedCity, setSeedCity] = useState("");
  const [seedIndustry, setSeedIndustry] = useState("");
  const [seedBusy, setSeedBusy] = useState(false);
  const [bulkUrls, setBulkUrls] = useState("");
  // Curated catalog state — bypasses search-engine bot blocks entirely.
  const [catalog, setCatalog] = useState(null);
  const [searchHealth, setSearchHealth] = useState(null);

  const loadTargets = async () => {
    try {
      const { data } = await api.get("/laie/targets");
      setTargets(data);
    } catch {}
  };
  useEffect(() => { loadTargets(); }, []);

  // Load static catalog + one-shot search-engine health probe on mount.
  useEffect(() => {
    api.get("/laie/catalog").then((r) => setCatalog(r.data)).catch(() => {});
    api.get("/laie/search/health").then((r) => setSearchHealth(r.data)).catch(() => {});
  }, []);

  // Poll any running job every 3 seconds
  useEffect(() => {
    if (!runningJob || runningJob.status !== "running") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/laie/discovery/jobs/${runningJob.id}`);
        setRunningJob(data);
        if (data.status !== "running") {
          if (data.status === "done") {
            const summary = data.summary || {};
            const totalNew = summary.total_new ?? (summary.created?.length ?? 0);
            toast.success(`Discovery complete · ${totalNew} new companies.`);
            loadTargets();
            onChange?.();
          } else if (data.status === "error") {
            toast.error(`Job failed: ${data.error || "unknown"}`);
          }
        }
      } catch {}
    }, 3000);
    return () => clearInterval(t);
  }, [runningJob?.id, runningJob?.status, onChange]);

  const createTarget = async () => {
    if (!newCity.trim() || newIndustries.length === 0) {
      toast.error("Pick a city and at least one industry.");
      return;
    }
    setCreating(true);
    try {
      await api.post("/laie/targets", {
        city: newCity.trim(),
        industries: newIndustries,
        max_results_per_query: 6,
      });
      setNewCity("");
      setNewIndustries([]);
      await loadTargets();
      toast.success("Discovery target added.");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create target.");
    }
    setCreating(false);
  };

  const removeTarget = async (id) => {
    try {
      await api.delete(`/laie/targets/${id}`);
      await loadTargets();
      toast.success("Target removed.");
    } catch { toast.error("Delete failed."); }
  };

  const runTarget = async (id) => {
    try {
      const { data } = await api.post("/laie/discovery/run", { target_id: id });
      if (data.already_running) {
        toast.info("A discovery job is already running — polling that one.");
      } else {
        toast.success("Discovery started — this can take 1-3 min. You can navigate away, it'll keep running.");
      }
      // Kick off polling
      const { data: job } = await api.get(`/laie/discovery/jobs/${data.job_id}`);
      setRunningJob(job);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start discovery.");
    }
  };

  const runCatalog = async (cities) => {
    // cities === undefined => all curated cities
    try {
      const { data } = await api.post("/laie/catalog/run", { cities });
      if (data.already_running) {
        toast.info("A job is already running — polling that one.");
      } else {
        toast.success(`Ingesting ${data.url_count} curated URL${data.url_count === 1 ? "" : "s"} — background job started.`);
      }
      const { data: job } = await api.get(`/laie/discovery/jobs/${data.job_id}`);
      setRunningJob(job);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Catalog run failed.");
    }
  };

  const seedOne = async () => {
    if (!seedUrl.trim()) { toast.error("Paste a URL first."); return; }
    setSeedBusy(true);
    try {
      const { data } = await api.post("/laie/companies/seed", {
        url: seedUrl.trim(),
        city: seedCity.trim() || undefined,
        industry: seedIndustry || undefined,
      });
      if (data.status === "duplicate") {
        toast.info(`Already in the database (${data.existing.source || "laie"}).`);
      } else {
        toast.success(`Ingested · ${data.company.company_name} · Grade ${data.company.grade}`);
      }
      setSeedUrl("");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ingest failed.");
    }
    setSeedBusy(false);
  };

  const seedBulk = async () => {
    const urls = bulkUrls.split(/\s+/).map((s) => s.trim()).filter(Boolean);
    if (urls.length === 0) { toast.error("Paste some URLs first."); return; }
    if (urls.length > 50) { toast.error("Max 50 URLs per batch."); return; }
    try {
      const { data } = await api.post("/laie/companies/seed-bulk", {
        urls,
        city: seedCity.trim() || undefined,
        industry: seedIndustry || undefined,
      });
      if (data.already_running) {
        toast.info("A job is already running — polling that one.");
      } else {
        toast.success(`Bulk ingest started · ${data.url_count} URLs. Keep this tab open to watch progress.`);
      }
      const { data: job } = await api.get(`/laie/discovery/jobs/${data.job_id}`);
      setRunningJob(job);
      setBulkUrls("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk seed failed.");
    }
  };

  const searchBlocked = searchHealth &&
    (searchHealth.urls_returned === 0) &&
    !searchHealth.serper_configured;

  return (
    <div className="space-y-4">
      {/* Search-engine health / kickstart catalog — surfaces the "0 leads" reality */}
      {searchBlocked && (
        <div
          className="rounded-2xl border border-chart-3/40 bg-chart-3/5 p-5"
          data-testid="laie-search-blocked-banner"
        >
          <div className="flex items-start gap-3">
            <div className="h-8 w-8 shrink-0 rounded-full bg-chart-3/15 grid place-items-center">
              <Radar className="h-4 w-4 text-chart-3" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.22em] text-chart-3 mb-1">
                Free-search fallback blocked
              </div>
              <div className="text-sm text-foreground/90 leading-relaxed">
                DuckDuckGo is currently returning bot-challenge responses to our server.
                That's why "Run discovery" comes back with 0 new leads — the search step itself
                gets blocked before we can enrich anything.
                <br />
                <b>Two paths forward, ready right now:</b>
              </div>
              <ul className="mt-2 ml-4 text-xs list-disc space-y-1 text-muted-foreground">
                <li>Use the <b className="text-foreground">Kickstart from catalog</b> below —
                    it bypasses search entirely with hand-curated India-focused URLs.</li>
                <li>Or grab a free <a href="https://serper.dev" target="_blank" rel="noopener noreferrer" className="text-primary underline">Serper.dev</a> key
                    (2,500 Google searches/month free) and set it as
                    <code className="mx-1">SERPER_API_KEY</code> in the environment — LAIE will use it automatically.</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Kickstart from curated catalog */}
      {catalog && catalog.cities.length > 0 && (
        <div className="rounded-2xl border border-primary/30 bg-primary/5 p-5" data-testid="laie-catalog-panel">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <h2 className="font-display text-xl flex items-center gap-2">
                <Rocket className="h-4 w-4 text-primary" />
                Kickstart from Kairosa's curated catalog
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                {catalog.total_urls} hand-picked Indian businesses across {catalog.cities.length} cities —
                one click ingests + enriches + scores them all. Ideal after a fresh deploy.
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="rounded-full"
              disabled={runningJob?.status === "running"}
              onClick={() => runCatalog()}
              data-testid="laie-catalog-run-all"
            >
              <Play className="h-3 w-3 mr-1.5" />
              Ingest ALL cities
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {catalog.cities.map((c) => (
              <button
                key={c.city}
                onClick={() => runCatalog([c.city])}
                disabled={runningJob?.status === "running"}
                data-testid={`laie-catalog-run-${c.city}`}
                className="text-left rounded-lg border border-border bg-background hover:border-primary/50 hover:bg-primary/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors px-3 py-2"
              >
                <div className="text-sm font-medium leading-none flex items-center gap-2">
                  {c.city}
                  <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    {c.count} URL{c.count === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="text-[10px] text-muted-foreground mt-1 truncate max-w-[180px]">
                  {c.industries.join(" · ")}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Backup / Restore — safety net so data can be preserved across redeploys */}
      <BackupPanel runningJob={runningJob} onRestore={onChange} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* LEFT — Discovery targets */}
      <div className="lg:col-span-2 space-y-4">
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <h2 className="font-display text-xl flex items-center gap-2">
                <Radar className="h-4 w-4 text-primary" /> Discovery targets
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                Pick a city + industries. LAIE builds free DuckDuckGo queries and enriches every unique domain it finds.
              </p>
            </div>
          </div>

          {/* Create target */}
          <div className="rounded-lg border border-border/70 bg-secondary/30 p-4 space-y-3 mb-4" data-testid="laie-create-target">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">City</Label>
                <Input
                  data-testid="laie-target-city"
                  list="laie-city-options"
                  value={newCity}
                  onChange={(e) => setNewCity(e.target.value)}
                  placeholder="e.g. Gurugram"
                  className="mt-1.5"
                />
                <datalist id="laie-city-options">
                  {meta?.city_phases &&
                    Object.entries(meta.city_phases).map(([c, p]) => (
                      <option key={c} value={c}>{`Phase ${p}`}</option>
                    ))}
                </datalist>
              </div>
              <div>
                <Label className="text-xs">Industries (click to toggle · min 1)</Label>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {(meta?.industries || []).map((ind) => {
                    const on = newIndustries.includes(ind);
                    return (
                      <button
                        key={ind}
                        type="button"
                        data-testid={`laie-industry-toggle-${ind}`}
                        onClick={() =>
                          setNewIndustries((prev) =>
                            prev.includes(ind) ? prev.filter((x) => x !== ind) : [...prev, ind],
                          )
                        }
                        className={
                          "text-[10px] uppercase tracking-[0.16em] rounded-full border px-2.5 py-1 transition-colors " +
                          (on
                            ? "border-primary bg-primary/15 text-primary"
                            : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground")
                        }
                      >
                        {ind}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <Button
              size="sm"
              className="rounded-full"
              data-testid="laie-target-create-btn"
              disabled={creating || !newCity.trim() || newIndustries.length === 0}
              onClick={createTarget}
            >
              <ListPlus className="h-3.5 w-3.5 mr-1.5" />
              {creating ? "Adding…" : "Add target"}
            </Button>
          </div>

          {/* Existing targets */}
          {targets.length === 0 ? (
            <EmptyBlock text="No discovery targets yet — add one above to unlock the “Run discovery” button." />
          ) : (
            <div className="space-y-2">
              {targets.map((t) => (
                <div
                  key={t.id}
                  data-testid={`laie-target-row-${t.id}`}
                  className="rounded-lg border border-border p-4 flex flex-wrap items-center gap-3"
                >
                  <div className="min-w-[180px]">
                    <div className="font-display text-lg leading-none">{t.city}</div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mt-1">
                      Phase {t.region_phase ?? "—"}
                    </div>
                  </div>
                  <div className="flex-1 flex flex-wrap gap-1.5">
                    {t.industries.map((i) => (
                      <span key={i} className="text-[10px] uppercase tracking-[0.16em] rounded-full border border-border px-2 py-0.5 text-muted-foreground">
                        {i}
                      </span>
                    ))}
                  </div>
                  {t.last_run_summary && (
                    <div className="text-[10px] text-muted-foreground">
                      last run · <b className="text-foreground">{t.last_run_summary.new}</b> new, {t.last_run_summary.duplicates} dup, {t.last_run_summary.failed} failed
                    </div>
                  )}
                  <Button
                    size="sm"
                    className="rounded-full"
                    data-testid={`laie-target-run-${t.id}`}
                    disabled={runningJob?.status === "running"}
                    onClick={() => runTarget(t.id)}
                  >
                    <Play className="h-3 w-3 mr-1.5" />
                    {runningJob?.status === "running" ? "Running…" : "Run discovery"}
                  </Button>
                  <button
                    onClick={() => removeTarget(t.id)}
                    data-testid={`laie-target-delete-${t.id}`}
                    title="Remove target"
                    className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Live job progress / result panel */}
          {runningJob && (
            <div
              className={
                "mt-4 rounded-lg border p-4 " +
                (runningJob.status === "running"
                  ? "border-primary/30 bg-primary/5"
                  : runningJob.status === "error"
                  ? "border-destructive/40 bg-destructive/5"
                  : "border-primary/30 bg-primary/5")
              }
              data-testid="laie-run-result"
            >
              <div className="flex items-center gap-2 mb-1.5">
                {runningJob.status === "running" && (
                  <RefreshCw className="h-3.5 w-3.5 animate-spin text-primary" />
                )}
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  {runningJob.kind === "bulk_seed" ? "Bulk ingest" : "Discovery"} · {runningJob.status}
                </div>
                <button
                  onClick={() => setRunningJob(null)}
                  className="ml-auto p-1 rounded hover:bg-secondary/60 text-muted-foreground"
                  title="Hide"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
              <div className="text-sm">
                <b className="text-primary">{runningJob.progress?.new || 0}</b> new ·{" "}
                <span className="text-muted-foreground">{runningJob.progress?.duplicates || 0} dup</span> ·{" "}
                <span className="text-muted-foreground">{runningJob.progress?.failed || 0} failed</span>
              </div>
              {runningJob.status === "running" && (
                <div className="text-[11px] text-muted-foreground mt-1">
                  Enriching each domain with Claude Sonnet 4.5 · ~15-25s per company. Feel free to navigate away — the job keeps running.
                </div>
              )}
              {runningJob.summary?.sample_new_companies?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {runningJob.summary.sample_new_companies.slice(0, 10).map((c) => (
                    <span key={c.id} className={"text-[10px] uppercase tracking-[0.16em] rounded-full border px-2 py-0.5 " + (GRADE_COLOR[c.grade] || "")}>
                      {c.grade} · {c.company_name}
                    </span>
                  ))}
                </div>
              )}
              {runningJob.error && (
                <div className="text-xs text-destructive mt-2">{runningJob.error}</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* RIGHT — Seed a URL */}
      <div className="space-y-4">
        <div className="rounded-2xl border border-border bg-card p-5">
          <h2 className="font-display text-xl mb-1 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" /> Seed a URL
          </h2>
          <p className="text-xs text-muted-foreground mb-4">
            Paste any business homepage — LAIE scrapes + Claude enriches + scores it in one click.
          </p>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Website URL</Label>
              <Input
                data-testid="laie-seed-url"
                value={seedUrl}
                onChange={(e) => setSeedUrl(e.target.value)}
                placeholder="https://…"
                className="mt-1.5"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">City (hint)</Label>
                <Input
                  data-testid="laie-seed-city"
                  value={seedCity}
                  onChange={(e) => setSeedCity(e.target.value)}
                  placeholder="e.g. Delhi"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label className="text-xs">Industry (hint)</Label>
                <Select value={seedIndustry} onValueChange={setSeedIndustry}>
                  <SelectTrigger data-testid="laie-seed-industry" className="mt-1.5">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    {(meta?.industries || []).map((i) => (
                      <SelectItem key={i} value={i}>{i}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button
              className="w-full rounded-full"
              data-testid="laie-seed-submit"
              disabled={seedBusy || !seedUrl.trim()}
              onClick={seedOne}
            >
              {seedBusy ? "Enriching (10-20s)…" : "Ingest URL"}
            </Button>
          </div>
        </div>

        {/* Bulk seed */}
        <div className="rounded-2xl border border-border bg-card p-5">
          <h2 className="font-display text-xl mb-1 flex items-center gap-2">
            <ListPlus className="h-4 w-4 text-primary" /> Bulk seed
          </h2>
          <p className="text-xs text-muted-foreground mb-3">
            Paste up to 50 URLs (one per line). Enriches each sequentially.
          </p>
          <Textarea
            data-testid="laie-bulk-urls"
            rows={5}
            value={bulkUrls}
            onChange={(e) => setBulkUrls(e.target.value)}
            placeholder={"https://cafe-1.com\nhttps://cafe-2.com\n…"}
            className="text-xs font-mono"
          />
          <Button
            className="w-full mt-3 rounded-full"
            variant="outline"
            data-testid="laie-bulk-submit"
            disabled={runningJob?.status === "running" || !bulkUrls.trim()}
            onClick={seedBulk}
          >
            {runningJob?.status === "running" ? "Job running…" : "Ingest batch"}
          </Button>
        </div>
      </div>
    </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  COMPANIES                                                                 */
/* -------------------------------------------------------------------------- */

function CompaniesTab({ meta, refreshCounter, onChange }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ city: "", industry: "", grade: "", status: "" });
  // null = default backend sort (score desc, then discovered desc). Anything
  // else is user-driven: "date_desc" | "date_asc" | "score_desc" | "score_asc".
  const [sort, setSort] = useState(null);
  // "Last 7 days" quick filter — when true we send `since` = now-7d ISO.
  const [last7, setLast7] = useState(false);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const buildParams = () => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    if (sort) params.set("sort", sort);
    if (last7) {
      const cutoff = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      params.set("since", cutoff);
    }
    return params;
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = buildParams();
      params.set("limit", "200");
      const { data } = await api.get(`/laie/companies?${params.toString()}`);
      setItems(data.items);
      setTotal(data.total);
    } catch { toast.error("Could not load companies."); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [refreshCounter]);

  const filterStr = useMemo(() => JSON.stringify(filters) + q + (sort || "") + (last7 ? "1" : "0"), [filters, q, sort, last7]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [filterStr]);

  // Click the Date header — toggle newest → oldest → default.
  const toggleDateSort = () => {
    setSort((prev) => (prev === "date_desc" ? "date_asc" : prev === "date_asc" ? null : "date_desc"));
  };

  const promote = async (id) => {
    try {
      const { data } = await api.post(`/laie/companies/${id}/promote`);
      if (data.already_promoted) {
        toast.info("Already in Growth Engine.");
      } else {
        toast.success("→ Growth Engine · lead created.");
      }
      load();
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Promote failed."); }
  };

  const remove = async (id, name) => {
    try {
      await api.delete(`/laie/companies/${id}`);
      toast.success(`${name} removed.`);
      load();
      onChange?.();
    } catch { toast.error("Delete failed."); }
  };

  const downloadCsv = async () => {
    const params = buildParams();
    const backend = process.env.REACT_APP_BACKEND_URL;
    const url = `${backend}/api/laie/export.csv?${params.toString()}`;
    // Use a hidden link so browser saves the file
    const a = document.createElement("a");
    a.href = url;
    a.credentials = "include";
    a.rel = "noopener";
    a.click();
    toast.success("CSV download started");
  };

  return (
    <div className="space-y-3">
      {/* Filter bar */}
      <div className="rounded-xl border border-border bg-card p-3 flex flex-wrap items-center gap-2" data-testid="laie-filters">
        <Input
          data-testid="laie-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, domain or website…"
          className="h-9 max-w-xs"
        />
        <Select value={filters.city} onValueChange={(v) => setFilters((f) => ({ ...f, city: v === "__all__" ? "" : v }))}>
          <SelectTrigger data-testid="laie-filter-city" className="h-9 w-40 text-xs"><SelectValue placeholder="City" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All cities</SelectItem>
            {meta?.city_phases && Object.keys(meta.city_phases).map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.industry} onValueChange={(v) => setFilters((f) => ({ ...f, industry: v === "__all__" ? "" : v }))}>
          <SelectTrigger data-testid="laie-filter-industry" className="h-9 w-44 text-xs"><SelectValue placeholder="Industry" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All industries</SelectItem>
            {(meta?.industries || []).map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.grade} onValueChange={(v) => setFilters((f) => ({ ...f, grade: v === "__all__" ? "" : v }))}>
          <SelectTrigger data-testid="laie-filter-grade" className="h-9 w-28 text-xs"><SelectValue placeholder="Grade" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All grades</SelectItem>
            {(meta?.grades || ["A", "B", "C"]).map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === "__all__" ? "" : v }))}>
          <SelectTrigger data-testid="laie-filter-status" className="h-9 w-36 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All statuses</SelectItem>
            {(meta?.statuses || []).map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <button
          type="button"
          onClick={() => setLast7((v) => !v)}
          data-testid="laie-filter-last7"
          aria-pressed={last7}
          className={
            "h-9 inline-flex items-center gap-1.5 rounded-full border px-3 text-[10px] uppercase tracking-[0.2em] transition-colors " +
            (last7
              ? "border-primary bg-primary/15 text-primary"
              : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground")
          }
        >
          <CalendarClock className="h-3 w-3" />
          Last 7 days
        </button>
        <div className="text-xs text-muted-foreground ml-auto">{loading ? "loading…" : `${total} companies`}</div>
        <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={downloadCsv} data-testid="laie-export-csv">
          <Download className="h-3.5 w-3.5" /> CSV
        </Button>
      </div>

      {/* Table */}
      {items.length === 0 && !loading ? (
        <EmptyBlock text="No companies match. Try clearing filters or run discovery." />
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="laie-companies-table">
          <div className="grid grid-cols-[minmax(200px,2fr)_110px_130px_70px_80px_120px_80px_1fr_auto] text-[10px] uppercase tracking-[0.16em] text-muted-foreground border-b border-border px-4 py-2.5">
            <div>Company</div>
            <div>City</div>
            <div>Industry</div>
            <div>Grade</div>
            <div>Score</div>
            <div>Status</div>
            <button
              type="button"
              onClick={toggleDateSort}
              data-testid="laie-sort-date"
              className={
                "inline-flex items-center gap-1 text-left uppercase tracking-[0.16em] text-[10px] transition-colors " +
                (sort === "date_desc" || sort === "date_asc"
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground")
              }
              title={
                sort === "date_desc" ? "Newest first — click for oldest first"
                : sort === "date_asc" ? "Oldest first — click for default sort"
                : "Sort by date (newest first)"
              }
            >
              Date
              {sort === "date_desc" && <ArrowDown className="h-3 w-3" />}
              {sort === "date_asc" && <ArrowUp className="h-3 w-3" />}
              {sort !== "date_desc" && sort !== "date_asc" && (
                <ArrowUpDown className="h-3 w-3 opacity-40" />
              )}
            </button>
            <div>Signals</div>
            <div></div>
          </div>
          <div className="divide-y divide-border">
            {items.map((c) => (
              <div
                key={c.id}
                data-testid={`laie-company-row-${c.id}`}
                className="grid grid-cols-[minmax(200px,2fr)_110px_130px_70px_80px_120px_80px_1fr_auto] items-center px-4 py-3 hover:bg-secondary/30"
              >
                <div className="min-w-0">
                  <button
                    onClick={() => setDetail(c)}
                    className="text-sm font-medium truncate hover:text-primary text-left w-full"
                    data-testid={`laie-open-detail-${c.id}`}
                  >
                    {c.company_name}
                  </button>
                  <div className="text-[11px] text-muted-foreground truncate">{c.domain}</div>
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {c.city || "—"}
                  {c.region_phase ? <span className="ml-1 text-[9px]">P{c.region_phase}</span> : null}
                </div>
                <div className="text-xs text-muted-foreground truncate">{c.industry || "—"}</div>
                <div>
                  <span className={"text-[11px] uppercase tracking-[0.2em] border rounded-full px-2 py-0.5 " + (GRADE_COLOR[c.grade] || "")}>
                    {c.grade || "—"}
                  </span>
                </div>
                <div className="text-sm font-semibold">{c.opportunity_score ?? "—"}</div>
                <div className="text-xs">
                  {c.growth_lead_id ? (
                    <span className="inline-flex items-center gap-1 text-primary">
                      <Rocket className="h-3 w-3" /> in growth
                    </span>
                  ) : (
                    <span className="text-muted-foreground">{c.status?.replace(/_/g, " ") || "new"}</span>
                  )}
                </div>
                <div
                  className="text-xs text-muted-foreground tabular-nums"
                  title={c.discovered_at || ""}
                  data-testid={`laie-company-date-${c.id}`}
                >
                  {formatShortDate(c.discovered_at)}
                </div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {(c.buying_signals || []).slice(0, 2).join(" · ") || "—"}
                </div>
                <div className="flex items-center gap-1 justify-end">
                  {c.website && (
                    <a
                      href={c.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1.5 rounded hover:bg-secondary/60 text-muted-foreground hover:text-foreground"
                      title="Open website"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                  <button
                    onClick={() => promote(c.id)}
                    data-testid={`laie-promote-${c.id}`}
                    disabled={!!c.growth_lead_id}
                    title={c.growth_lead_id ? "Already promoted" : "Promote to Growth Engine"}
                    className="p-1.5 rounded hover:bg-primary/10 text-primary disabled:opacity-30 disabled:hover:bg-transparent"
                  >
                    <Rocket className="h-3.5 w-3.5" />
                  </button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        data-testid={`laie-delete-${c.id}`}
                        title="Delete"
                        className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete {c.company_name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Removes this company from LAIE. The Growth Engine lead (if promoted) stays untouched.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => remove(c.id, c.company_name)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Company detail slide-over (dialog) */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="laie-company-detail">
          {detail && <CompanyDetail company={detail} onChange={() => { load(); onChange?.(); }} onClose={() => setDetail(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CompanyDetail({ company, onChange, onClose }) {
  const [c, setC] = useState(company);
  const ai = c.ai_summary || {};

  const patch = async (delta) => {
    try {
      const { data } = await api.patch(`/laie/companies/${c.id}`, delta);
      setC(data);
      onChange?.();
      toast.success("Saved.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
  };

  const promote = async () => {
    try {
      const { data } = await api.post(`/laie/companies/${c.id}/promote`);
      if (data.already_promoted) toast.info("Already promoted.");
      else toast.success("→ Growth Engine · lead created.");
      const { data: fresh } = await api.get(`/laie/companies/${c.id}`);
      setC(fresh);
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Promote failed."); }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display text-2xl leading-tight flex items-baseline gap-3 pr-6">
          {c.company_name}
          <span className={"text-[11px] uppercase tracking-[0.2em] border rounded-full px-2 py-0.5 " + (GRADE_COLOR[c.grade] || "")}>
            {c.grade} · {c.opportunity_score}
          </span>
        </DialogTitle>
        <DialogDescription>
          {c.industry} · {c.city || "—"} · <a href={c.website} target="_blank" rel="noopener noreferrer" className="underline">{c.domain}</a>
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 pt-2">
        {/* Summary */}
        {ai.summary && (
          <div>
            <SectionLabel>Executive summary</SectionLabel>
            <p className="text-sm leading-relaxed">{ai.summary}</p>
          </div>
        )}

        {/* Signals grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <MicroCard label="Buying potential" value={ai.buying_potential || "unknown"} />
          <MicroCard label="Growth stage" value={ai.growth_stage || "unknown"} />
          <MicroCard label="Wellness pot." value={ai.corporate_wellness_potential || "unknown"} />
          <MicroCard label="Subscription pot." value={ai.coffee_subscription_potential || "unknown"} />
        </div>

        {/* Contact */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <SectionLabel>Emails</SectionLabel>
            <div>{c.emails?.join(", ") || <span className="text-muted-foreground">—</span>}</div>
          </div>
          <div>
            <SectionLabel>Phones</SectionLabel>
            <div>{c.phones?.join(", ") || <span className="text-muted-foreground">—</span>}</div>
          </div>
          <div>
            <SectionLabel>LinkedIn</SectionLabel>
            <div className="truncate">
              {c.linkedin_url ? <a className="underline" target="_blank" rel="noopener noreferrer" href={c.linkedin_url}>{c.linkedin_url}</a> : <span className="text-muted-foreground">—</span>}
            </div>
          </div>
          <div>
            <SectionLabel>Instagram</SectionLabel>
            <div className="truncate">
              {c.instagram_url ? <a className="underline" target="_blank" rel="noopener noreferrer" href={c.instagram_url}>{c.instagram_url}</a> : <span className="text-muted-foreground">—</span>}
            </div>
          </div>
        </div>

        {/* Signal lists */}
        {(ai.buying_signals?.length > 0 || ai.expansion_signals?.length > 0 || ai.sustainability_signals?.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <SignalList label="Buying signals" tone="primary" items={ai.buying_signals || []} />
            <SignalList label="Expansion signals" tone="chart-3" items={ai.expansion_signals || []} />
            <SignalList label="Sustainability" tone="muted" items={ai.sustainability_signals || []} />
          </div>
        )}

        {/* Decision makers */}
        {c.decision_makers?.length > 0 && (
          <div>
            <SectionLabel>Decision makers (public)</SectionLabel>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {c.decision_makers.map((dm, i) => (
                <div key={i} className="text-xs border border-border rounded-md p-2">
                  <div className="font-medium">{dm.name || "—"}</div>
                  <div className="text-muted-foreground">{dm.designation}</div>
                  {dm.linkedin_url && <a href={dm.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-primary underline">LinkedIn</a>}
                  {dm.email && <div className="truncate">{dm.email}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CRM */}
        <div>
          <SectionLabel>CRM</SectionLabel>
          <div className="grid grid-cols-2 gap-2">
            <Select value={c.status || "new"} onValueChange={(v) => patch({ status: v })}>
              <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["new","contacted","qualified","meeting_scheduled","quotation_sent","sample_sent","negotiation","won","lost","dormant"].map((s) => (
                  <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={c.next_follow_up_date?.slice(0, 10) || ""}
              onChange={(e) => patch({ next_follow_up_date: e.target.value ? new Date(e.target.value).toISOString() : "" })}
              className="h-9 text-xs"
              data-testid="laie-detail-followup"
            />
          </div>
        </div>

        {/* Manual intelligence */}
        <div>
          <SectionLabel>Manual intelligence (notes only you see)</SectionLabel>
          <Textarea
            rows={3}
            defaultValue={c.manual_intelligence || ""}
            onBlur={(e) => {
              if (e.target.value !== (c.manual_intelligence || "")) patch({ manual_intelligence: e.target.value });
            }}
            placeholder="Anything you know that Claude can use next time you re-enrich…"
            className="text-xs"
            data-testid="laie-detail-notes"
          />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t border-border">
          <Button
            className="rounded-full gap-1.5"
            onClick={promote}
            disabled={!!c.growth_lead_id}
            data-testid="laie-detail-promote"
          >
            <Rocket className="h-4 w-4" />
            {c.growth_lead_id ? "Already in Growth Engine" : "Promote to Growth Engine"}
          </Button>
          {c.growth_lead_id && (
            <a
              href={`/app/growth/leads/${c.growth_lead_id}`}
              className="text-xs text-primary inline-flex items-center gap-1 underline"
            >
              Open in Growth <ArrowUpRight className="h-3 w-3" />
            </a>
          )}
          <button
            onClick={onClose}
            className="ml-auto p-1.5 rounded hover:bg-secondary/60 text-muted-foreground"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  );
}

/* ---------- Tiny reusable atoms ---------- */

function Kpi({ testid, icon, label, value, hint, tone }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className={"font-display text-3xl mt-2 " + (tone === "primary" ? "text-primary" : "")}>
        {value}
      </div>
      {hint && <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function EmptyBlock({ text }) {
  return <div className="text-sm text-muted-foreground text-center py-8 border border-dashed border-border rounded-lg">{text}</div>;
}

function SectionLabel({ children }) {
  return <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1.5">{children}</div>;
}

function MicroCard({ label, value }) {
  return (
    <div className="rounded-md border border-border p-2">
      <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="text-sm font-medium capitalize">{value}</div>
    </div>
  );
}

function SignalList({ label, tone, items }) {
  if (!items || items.length === 0) return null;
  const toneClass = tone === "primary" ? "text-primary" : tone === "chart-3" ? "text-chart-3" : "text-muted-foreground";
  return (
    <div>
      <SectionLabel>{label}</SectionLabel>
      <ul className="text-xs space-y-1">
        {items.map((s, i) => (
          <li key={i} className="flex gap-2">
            <span className={"shrink-0 " + toneClass}>•</span>
            <span>{s}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}


/**
 * BackupPanel — safety net so LAIE data can survive any redeploy, migration,
 * or accidental delete. Two actions:
 *   • Download JSON snapshot of all LAIE data (companies + targets + jobs).
 *   • Restore from a previously downloaded snapshot ("merge" mode is safe —
 *     only inserts missing rows, never overwrites existing data).
 */
function BackupPanel({ runningJob, onRestore }) {
  const [busy, setBusy] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreMode, setRestoreMode] = useState("merge");
  const [file, setFile] = useState(null);

  const downloadBackup = async () => {
    setBusy(true);
    try {
      const res = await api.get("/laie/backup.json", { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.download = `kairosa-laie-backup-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Backup downloaded — keep this file safe.");
    } catch {
      toast.error("Backup failed.");
    }
    setBusy(false);
  };

  const submitRestore = async () => {
    if (!file) { toast.error("Choose a backup .json file first."); return; }
    setBusy(true);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const { data } = await api.post("/laie/backup/restore", {
        payload: parsed,
        mode: restoreMode,
        confirm: restoreMode === "replace",
      });
      const s = data.stats || {};
      const inserted = Object.values(s).reduce((n, v) => n + (v.inserted || 0), 0);
      const skipped = Object.values(s).reduce((n, v) => n + (v.skipped || 0), 0);
      toast.success(`Restore complete · ${inserted} inserted · ${skipped} skipped (${restoreMode} mode)`);
      setRestoreOpen(false);
      setFile(null);
      onRestore?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Restore failed.");
    }
    setBusy(false);
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="laie-backup-panel">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 shrink-0 rounded-full bg-primary/10 grid place-items-center">
          <ShieldCheck className="h-4 w-4 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline flex-wrap gap-3">
            <h2 className="font-display text-xl">Data safety · Backup & restore</h2>
            <span className="text-[10px] uppercase tracking-[0.22em] text-primary">
              Redeploy-proof
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Your LAIE data lives in a persistent MongoDB and <b className="text-foreground">never gets wiped on redeploy</b> —
            deployment pushes code only. This panel is a belt-and-braces safety net: download a full snapshot at any time,
            and if anything ever does go wrong you can restore in seconds.
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className="rounded-full gap-1.5"
          onClick={downloadBackup}
          disabled={busy || runningJob?.status === "running"}
          data-testid="laie-backup-download"
        >
          <Download className="h-3.5 w-3.5" />
          {busy ? "Preparing…" : "Download JSON backup"}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="rounded-full gap-1.5 text-muted-foreground"
          onClick={() => setRestoreOpen(true)}
          data-testid="laie-backup-restore-open"
        >
          <Upload className="h-3.5 w-3.5" />
          Restore from file…
        </Button>
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground ml-auto">
          Companies · Targets · Job history
        </div>
      </div>

      <Dialog open={restoreOpen} onOpenChange={(o) => { setRestoreOpen(o); if (!o) setFile(null); }}>
        <DialogContent data-testid="laie-restore-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Restore LAIE data</DialogTitle>
            <DialogDescription>
              Upload a <code>kairosa-laie-backup-*.json</code> file downloaded from this panel.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Backup file</Label>
              <Input
                type="file"
                accept=".json,application/json"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-1.5"
                data-testid="laie-restore-file"
              />
              {file && (
                <div className="text-[11px] text-muted-foreground mt-1">
                  {file.name} · {(file.size / 1024).toFixed(1)} KB
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Mode</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setRestoreMode("merge")}
                  className={
                    "rounded-lg border p-3 text-left transition-colors " +
                    (restoreMode === "merge"
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-primary/40")
                  }
                  data-testid="laie-restore-mode-merge"
                >
                  <div className="text-sm font-medium">Merge (safe)</div>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Only inserts rows that aren't already in the DB. Never overwrites or deletes.
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setRestoreMode("replace")}
                  className={
                    "rounded-lg border p-3 text-left transition-colors " +
                    (restoreMode === "replace"
                      ? "border-destructive bg-destructive/10"
                      : "border-border hover:border-destructive/40")
                  }
                  data-testid="laie-restore-mode-replace"
                >
                  <div className="text-sm font-medium text-destructive">Replace (destructive)</div>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Wipes current LAIE tables first, then loads the snapshot. Use to fully roll back.
                  </div>
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRestoreOpen(false)} disabled={busy}>Cancel</Button>
            <Button
              onClick={submitRestore}
              disabled={busy || !file}
              className={restoreMode === "replace" ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
              data-testid="laie-restore-submit"
            >
              {busy ? "Restoring…" : (restoreMode === "replace" ? "Replace & restore" : "Merge & restore")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
